import React from 'react';
import { Box, Button } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { serifVoice } from '../../theme';

const HERO = '/homepage.jpg';

/**
 * The public front door.
 *
 * The wordmark and tagline are part of the photograph, not DOM — so this page
 * deliberately adds no heading of its own. Two consequences it has to respect:
 *
 * 1. The image must never be cropped. A full-bleed `cover` looks right on a
 *    desktop (the photo is 3:2, close to a laptop viewport) but on a 390px
 *    phone it shows barely a third of the image width and clips "Roamoir" at
 *    both ends. So the sharp copy is `contain`ed, and a blurred, scaled copy of
 *    the same file fills whatever space is left. Edge to edge at any aspect
 *    ratio, and the brand never gets cut.
 * 2. The alt text has to carry the words in the picture, or a screen reader
 *    gets a page with no name on it.
 */
export default function HomePage() {
  const { session } = useAuth();

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        width: '100%',
        overflow: 'hidden',
        bgcolor: 'primary.main',
      }}
    >
      {/* Blurred fill — same asset, so no extra download. Scaled up so the
          blur's soft edges never reach the viewport border. */}
      <Box
        component="img"
        src={HERO}
        alt=""
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          filter: 'blur(48px) saturate(1.15)',
          transform: 'scale(1.15)',
        }}
      />

      {/* Forest scrim over the blur. Without it the out-of-focus bands read as
          muddy grey-green; with it they read as the brand ground, and the
          sharp photo sits on them like a print on a page. */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          bgcolor: 'primary.main',
          opacity: { xs: 0.42, md: 0.22 },
        }}
      />

      {/* The photograph itself, never cropped.
          On a phone the 3:2 photo can only ever be a band — cropping it to fill
          a 0.46-aspect viewport would cut "Roamoir" off at both ends. So it
          sits a little above centre, which leaves the larger margin at the
          bottom and reads as a composed page rather than a mistake. */}
      <Box
        component="img"
        src={HERO}
        alt="Roamoir — go somewhere, keep the story. A lake at sunrise, seen from a stone terrace."
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          objectPosition: { xs: 'center 38%', md: 'center' },
        }}
      />

      {/* Phone only. The contained photo leaves a tall margin below it that no
          amount of positioning removes — a 3:2 image on a 0.46-aspect screen.
          One line of Roamoir's own voice turns that margin into part of the
          page. Hidden from md up, where the photo already fills the frame. */}
      <Box
        sx={{
          display: { xs: 'block', md: 'none' },
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          px: 4,
          pb: 6,
          textAlign: 'center',
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            ...serifVoice,
            color: 'rgba(232,244,236,0.88)',
            fontSize: '1.05rem',
            lineHeight: 1.7,
          }}
        >
          A travel journal that plans the trip with you, and keeps up when
          there’s no signal.
        </Box>
      </Box>

      {/* Sign in, top right. Solid forest rather than a translucent pill —
          the top-right of the photo is bright sky and low-contrast glass
          would disappear into it. */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          p: { xs: 2, sm: 3 },
          zIndex: 1,
        }}
      >
        <Button
          component={RouterLink}
          to={session ? '/trips' : '/login'}
          variant="contained"
          sx={{
            bgcolor: 'primary.main',
            color: '#FFFFFF',
            boxShadow: '0 2px 12px rgba(44,31,20,0.28)',
            '&:hover': { bgcolor: 'primary.light' },
          }}
        >
          {session ? 'My trips' : 'Sign in'}
        </Button>
      </Box>
    </Box>
  );
}
